import React, { useState, useEffect, useContext } from 'react';
import { getAhaMoment, submitAhaMomentRating, getUserConsent } from '../../../Api/apiUtils';
import { toast } from 'react-toastify';
import './ahamoment.scss';
import { PlayCircle, Download } from 'react-feather';
import { Page, PageHeader, Card, Button, Field, Select, TextInput, Toolbar, Badge, BadgeTone, Loader, EmptyState, Dialog } from '../../../ui';
import { ConfigContext } from '../../../Api/global/ApiContext';


interface AhaMomentItem {
  fileName: string;
  url: string;
  thumbnailUrl: string;
  userId: string;
  studentId: string;
  studentName: string;
  studentGender: string;
  studentAgeGroup: string;
  teacherName: string;
  course: string;
  subType: string;
  ratings?: number;
  consentGiven?: string;
}

interface Course {
  name: string;
  id: string;
  v2Id: string;
  image?: string;
  colouredImage?: string;
  tags?: any[];
}

interface AgeGroup {
  name: string;
}

const AhaMoment: React.FC = () => {
    const config = useContext(ConfigContext)
    const { grmpcourses = [], ageGroups =[]} = config || {};
    
  const [ahaMoments, setAhaMoments] = useState<AhaMomentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [ratings, setRatings] = useState<{ [key: number]: number }>({});
  
  // Filter states
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('');
  const [selectedRating, setSelectedRating] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (isPagination: boolean = false) => {
    setLoading(true);
    try {
      const filters: any = {};
      if (selectedCourseId) filters.courseId = selectedCourseId;
      if (selectedGender) filters.studentGender = selectedGender;
      if (selectedAgeGroup) filters.studentAgeGroup = selectedAgeGroup;
      if (selectedRating) filters.ratings = selectedRating;

      const dateRange = startDate && endDate ? { startDate, endDate } : undefined;

      const response = await getAhaMoment(
        20,
        isPagination ? JSON.stringify(lastEvaluatedKey) : undefined,
        Object.keys(filters).length > 0 ? filters : undefined,
        dateRange
      );

      if (response?.data?.Items && response.data.Items.length > 0) {
        const momentsData = response.data.Items.map((moment: AhaMomentItem) => ({
          ...moment,
          ratings: moment.ratings || 0,
        }));

        const filteredMoments = momentsData.filter((moment: AhaMomentItem) => {
          if (moment.subType === 'Student') {
            return moment.userId === moment.studentId;
          }
          return true;
        });

        if (isPagination) {
          setAhaMoments((prev) => [...prev, ...filteredMoments]);
        } else {
          setAhaMoments(filteredMoments);
        }

        setLastEvaluatedKey(response.data.LastEvaluatedKey);

        // Update ratings
        const updatedRatings = { ...ratings };
        filteredMoments.forEach((moment: AhaMomentItem, index: number) => {
          const currentIndex = isPagination ? ahaMoments.length + index : index;
          updatedRatings[currentIndex] = moment.ratings || 0;
        });
        setRatings(updatedRatings);
      } else {
        if (!isPagination) {
          setAhaMoments([]);
          toast.info('No aha moments found');
        }
      }
    } catch (error) {
      console.error('Error fetching aha moments:', error);
      toast.error('Failed to fetch aha moments');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = async (index: number, value: number) => {
    const updatedRatings = { ...ratings };
    updatedRatings[index] = value;
    setRatings(updatedRatings);

    const momentData = ahaMoments[index];
    try {
      await submitAhaMomentRating({
        fileName: momentData.fileName,
        userId: momentData.userId,
        ratings: value,
        user: momentData.studentName,
      });
      toast.success('Rating submitted successfully!');
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating');
    }
  };

  const handleConsent = async (index: number, moment: AhaMomentItem) => {
    try {
      const response = await getUserConsent(moment.userId);
      const consentStatus = response?.studentProfile?.tncAccepted;

      setAhaMoments((prevMoments) => {
        const updatedMoments = [...prevMoments];
        updatedMoments[index].consentGiven =
          consentStatus === true ? 'green' : consentStatus === false ? 'red' : 'darkorange';
        return updatedMoments;
      });
    } catch (error) {
      console.error('Error fetching consent:', error);
      toast.error('Failed to fetch consent status');
    }
  };

  const downloadVideo = (moment: AhaMomentItem) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = moment.url;
    downloadLink.download = moment.fileName || 'video.mp4';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handlePlayerIconClick = (videoUrl: string, index: number) => {
    setSelectedVideoUrl(videoUrl);
    setSelectedIndex(index);
    setTimeout(() => {
      setModalVisible(true);
    }, 100);
  };

  const handleLoadMore = () => {
    if (lastEvaluatedKey) {
      fetchData(true);
    }
  };

  const handleSubmit = () => {
    setLastEvaluatedKey(null);
    fetchData(false);
  };

  const renderStars = (value: number, onChange?: (value: number) => void) => {
    if (!onChange) {
      return (
        <div className="star-rating" aria-label={value ? `Rated ${value} of 5` : 'Not rated'}>
          {[1, 2, 3, 4, 5].map((star) => (
            <span key={star} className={`star ${star <= value ? 'filled' : ''}`} aria-hidden>★</span>
          ))}
        </div>
      );
    }
    return (
      <div className="star-rating star-rating--input" role="group" aria-label="Rate this moment">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star ${star <= value ? 'filled' : ''}`}
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
            aria-pressed={star <= value}
            onClick={() => onChange(star)}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  const CONSENT: Record<string, { label: string; tone: BadgeTone }> = {
    green: { label: 'Consent accepted', tone: 'success' },
    red: { label: 'Consent denied', tone: 'danger' },
    darkorange: { label: 'Consent pending', tone: 'warning' },
  };

  const courseOptions = [{ value: '', label: 'All courses' }, ...(grmpcourses || []).map((c: any) => ({ value: String(c.v2Id), label: c.name }))];
  const genderOptions = [{ value: '', label: 'All' }, { value: 'Male', label: 'Male' }, { value: 'Female', label: 'Female' }];
  const ageOptions = [{ value: '', label: 'All' }, ...(ageGroups || []).map((a: any) => ({ value: a.name, label: a.name }))];
  const ratingOptions = [{ value: '', label: 'All' }, ...[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} star${n > 1 ? 's' : ''}` }))];

  return (
    <Page>
      <PageHeader eyebrow="Marketing" title="Aha Moments" subtitle="Short class clips flagged as learner breakthroughs." />

      <Card>
        <Toolbar>
          <Field label="From">
            {(ctl) => <TextInput {...ctl} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />}
          </Field>
          <Field label="To">
            {(ctl) => <TextInput {...ctl} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />}
          </Field>
          <Field label="Course">
            {(ctl) => <Select {...ctl} options={courseOptions} value={selectedCourseId} onChange={(e) => setSelectedCourseId(e.target.value)} />}
          </Field>
          <Field label="Gender">
            {(ctl) => <Select {...ctl} options={genderOptions} value={selectedGender} onChange={(e) => setSelectedGender(e.target.value)} />}
          </Field>
          <Field label="Age group">
            {(ctl) => <Select {...ctl} options={ageOptions} value={selectedAgeGroup} onChange={(e) => setSelectedAgeGroup(e.target.value)} />}
          </Field>
          <Field label="Rating">
            {(ctl) => (
              <Select
                {...ctl}
                options={ratingOptions}
                value={selectedRating ? String(selectedRating) : ''}
                onChange={(e) => setSelectedRating(e.target.value ? Number(e.target.value) : undefined)}
              />
            )}
          </Field>
          <div className="ds-toolbar__end">
            <Button variant="primary" onClick={handleSubmit}>Apply filters</Button>
          </div>
        </Toolbar>
      </Card>

      {loading && ahaMoments.length === 0 ? (
        <Loader label="Loading aha moments…" />
      ) : ahaMoments.length > 0 ? (
        <>
          <div className="aha-grid">
            {ahaMoments.map((moment, index) => {
              const consent = moment.consentGiven ? CONSENT[moment.consentGiven] : undefined;
              return (
                <article key={index} className="aha-card">
                  <button type="button" className="aha-card__media" onClick={() => handlePlayerIconClick(moment.url, index)} aria-label={`Play clip of ${moment.studentName}`}>
                    <img src={moment.thumbnailUrl} alt="" className="aha-card__thumb" />
                    <PlayCircle className="aha-card__play" size={44} aria-hidden />
                  </button>
                  <div className="aha-card__body">
                    <div className="aha-card__consent">
                      {consent ? (
                        <Badge tone={consent.tone}>{consent.label}</Badge>
                      ) : (
                        <Button size="sm" variant="secondary" onClick={() => handleConsent(index, moment)}>Check consent</Button>
                      )}
                      {moment.consentGiven === 'green' && (
                        <Button size="sm" variant="ghost" icon={<Download size={14} aria-hidden />} onClick={() => downloadVideo(moment)}>
                          Download
                        </Button>
                      )}
                    </div>
                    <dl className="aha-card__facts">
                      <dt>Student</dt><dd>{moment.studentName}</dd>
                      <dt>Teacher</dt><dd>{moment.teacherName}</dd>
                      <dt>Course</dt><dd>{moment.course}</dd>
                      <dt>Age group</dt><dd>{moment.studentAgeGroup}</dd>
                      <dt>Gender</dt><dd>{moment.studentGender}</dd>
                    </dl>
                    {renderStars(ratings[index] || 0)}
                  </div>
                </article>
              );
            })}
          </div>

          {lastEvaluatedKey && (
            <div className="aha-more">
              <Button variant="secondary" onClick={handleLoadMore} loading={loading}>Load more</Button>
            </div>
          )}
        </>
      ) : (
        <EmptyState title="No aha moments found" description="Try a wider date range or clear some filters." />
      )}

      <Dialog open={modalVisible} onClose={() => setModalVisible(false)} title="Aha moment" size="lg">
        {selectedIndex !== null && (
          <div className="aha-rate">
            <span className="ds-text-muted">Your rating</span>
            {renderStars(ratings[selectedIndex] || 0, (value) => handleRatingChange(selectedIndex, value))}
          </div>
        )}
        <video className="aha-video" controls autoPlay controlsList="nodownload">
          <source src={selectedVideoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </Dialog>
    </Page>
  );
};

export default AhaMoment;